import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment/min/moment-with-locales';
import { api } from 'common';
import {
  Alert,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import VolunteerActivismOutlinedIcon from '@mui/icons-material/VolunteerActivismOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import C2Dialog, { C2SecondaryButton } from '../C2Dialog';
import { C2, C2_FONT, C2Select, C2SummaryCard, C2StatusChip, C2DocsChip } from '../Table/C2UserTable';
import C2ReportTable, { C2Cell, formatCop } from './C2ReportTable';

const LEVELS = [1, 2, 3];
const EMPTY_MONTH = { billed: [0, 0, 0], recharges: [0, 0, 0], bonus: [0, 0, 0] };
const LEVEL_PERCENT = { 1: 8, 2: 6, 3: 2 };

const sum = (arr) => arr.reduce((acc, v) => acc + (Number(v) || 0), 0);

const roleLabel = (d) => {
  if (d.isLeader) return 'Líder';
  if (d.networkLevel != null) return `Nivel ${d.networkLevel}`;
  return 'Independiente';
};

function LevelCell({ count, billed, recharges, bonus }) {
  return (
    <Box sx={{ textAlign: 'left', minWidth: 150 }}>
      <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, fontSize: '0.9rem', color: C2.sidebar }}>
        {count} {count === 1 ? 'conductor' : 'conductores'}
      </Typography>
      <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.78rem', color: C2.dark, whiteSpace: 'nowrap' }}>
        Facturado <b>{formatCop(billed)}</b>
      </Typography>
      <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.74rem', color: C2.muted, whiteSpace: 'nowrap' }}>
        {recharges} {recharges === 1 ? 'recarga' : 'recargas'} · Bono <span style={{ color: C2.violet, fontWeight: 700 }}>{formatCop(bonus)}</span>
      </Typography>
    </Box>
  );
}

const headCellSx = {
  fontFamily: C2_FONT,
  fontWeight: 700,
  fontSize: '0.76rem',
  color: C2.sidebar,
  backgroundColor: C2.lavender,
  borderBottom: `1px solid ${C2.border}`,
  whiteSpace: 'nowrap',
};
const bodyCellSx = { fontFamily: C2_FONT, fontSize: '0.82rem', color: C2.dark, borderBottom: `1px solid ${C2.cellBorder}`, whiteSpace: 'nowrap' };

function MonthlyBreakdown({ driver, months }) {
  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
        {LEVELS.map((lvl) => (
          <C2StatusChip key={lvl} tone="violet" dot={false} label={`Nivel ${lvl}: ${driver.counts[lvl - 1]} conductores`} />
        ))}
      </Box>
      <TableContainer sx={{ border: `1px solid ${C2.border}`, borderRadius: '12px', maxHeight: 420 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Mes</TableCell>
              {LEVELS.map((lvl) => (
                <TableCell key={lvl} sx={headCellSx} align="right">Facturado N{lvl}</TableCell>
              ))}
              <TableCell sx={headCellSx} align="right">Total facturado</TableCell>
              <TableCell sx={headCellSx} align="right">Bono ganado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {months.map((mk) => {
              const m = driver.months[mk] || EMPTY_MONTH;
              const total = sum(m.billed);
              const bonus = sum(m.bonus);
              const idle = total === 0 && bonus === 0;
              return (
                <TableRow key={mk} sx={{ backgroundColor: idle ? '#FFFFFF' : C2.bg }}>
                  <TableCell sx={{ ...bodyCellSx, fontWeight: 700, color: C2.sidebar }}>{api.reportMonthLabel(mk)}</TableCell>
                  {LEVELS.map((lvl) => (
                    <TableCell key={lvl} sx={{ ...bodyCellSx, color: m.billed[lvl - 1] ? C2.dark : C2.muted }} align="right">
                      {formatCop(m.billed[lvl - 1])}
                    </TableCell>
                  ))}
                  <TableCell sx={{ ...bodyCellSx, fontWeight: 800, color: C2.sidebar }} align="right">{formatCop(total)}</TableCell>
                  <TableCell sx={{ ...bodyCellSx, fontWeight: 700, color: bonus ? '#15803D' : C2.muted }} align="right">{formatCop(bonus)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

/**
 * Admin: por cada conductor, conductores en sus niveles 1-3, recargas (facturado) por nivel y
 * bono de red ganado, mes a mes.
 */
export default function NetworkLevelReport() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [month, setMonth] = useState('');
  const [city, setCity] = useState('');
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await api.fetchNetworkLevelReport();
    setLoading(false);
    if (res?.success) {
      setReport(res);
      setMonth((cur) => (cur && res.months.includes(cur) ? cur : res.months[0]));
    } else {
      setError(res?.error || 'No se pudo generar el reporte.');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const monthOpts = useMemo(
    () => (report?.months || []).map((mk) => ({ value: mk, label: api.reportMonthLabel(mk) })),
    [report]
  );

  const cityOpts = useMemo(() => {
    const map = {};
    (report?.drivers || []).forEach((d) => {
      if (d.cityId) map[d.cityId] = d.cityName || d.cityId;
    });
    return [
      { value: '', label: 'Todas las ciudades' },
      ...Object.keys(map)
        .map((id) => ({ value: id, label: map[id] }))
        .sort((a, b) => a.label.localeCompare(b.label, 'es')),
    ];
  }, [report]);

  const rows = useMemo(() => {
    if (!report || !month) return [];
    return report.drivers
      .filter((d) => !city || d.cityId === city)
      .map((d) => {
        const m = d.months[month] || EMPTY_MONTH;
        const row = {
          id: d.uid,
          driver: d,
          name: d.name,
          email: d.email,
          cityName: d.cityName,
          role: roleLabel(d),
          totalDrivers: sum(d.counts),
          totalBilled: sum(m.billed),
          totalBonus: sum(m.bonus),
        };
        LEVELS.forEach((lvl) => {
          row[`l${lvl}Count`] = d.counts[lvl - 1];
          row[`l${lvl}Billed`] = m.billed[lvl - 1];
          row[`l${lvl}Recharges`] = m.recharges[lvl - 1];
          row[`l${lvl}Bonus`] = m.bonus[lvl - 1];
        });
        return row;
      })
      .sort((a, b) => b.totalBilled - a.totalBilled || b.totalDrivers - a.totalDrivers);
  }, [report, month, city]);

  const summary = useMemo(() => {
    const t = (report?.totals && report.totals[month]) || { recharges: 0, rechargeCount: 0, bonus: 0 };
    return {
      drivers: rows.length,
      referred: rows.reduce((acc, r) => acc + r.l1Count, 0),
      recharges: t.recharges,
      rechargeCount: t.rechargeCount,
      bonus: city ? rows.reduce((acc, r) => acc + r.totalBonus, 0) : t.bonus,
    };
  }, [report, month, rows, city]);

  const levelColumn = (lvl) => ({
    title: `Nivel ${lvl} (${LEVEL_PERCENT[lvl]}%)`,
    field: `l${lvl}Billed`,
    type: 'numeric',
    export: false,
    render: (r) => (
      <LevelCell count={r[`l${lvl}Count`]} billed={r[`l${lvl}Billed`]} recharges={r[`l${lvl}Recharges`]} bonus={r[`l${lvl}Bonus`]} />
    ),
  });

  const exportColumns = LEVELS.flatMap((lvl) => [
    { title: `Conductores N${lvl}`, field: `l${lvl}Count`, hidden: true, export: true },
    { title: `Facturado N${lvl}`, field: `l${lvl}Billed`, hidden: true, export: true },
    { title: `Recargas N${lvl}`, field: `l${lvl}Recharges`, hidden: true, export: true },
    { title: `Bono N${lvl}`, field: `l${lvl}Bonus`, hidden: true, export: true },
  ]);

  const columns = [
    {
      title: 'Conductor',
      field: 'name',
      render: (r) => (
        <Box sx={{ textAlign: 'left', minWidth: 170 }}>
          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.9rem', color: C2.sidebar }}>{r.name}</Typography>
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.75rem', color: C2.muted, mb: 0.5 }}>{r.cityName || 'Sin ciudad'}</Typography>
          <C2StatusChip tone={r.driver.isLeader ? 'violet' : 'neutral'} dot={false} label={r.role} />
        </Box>
      ),
    },
    { title: 'Correo', field: 'email', hidden: true, export: true },
    { title: 'Ciudad', field: 'cityName', hidden: true, export: true },
    { title: 'Rol', field: 'role', hidden: true, export: true },
    levelColumn(1),
    levelColumn(2),
    levelColumn(3),
    {
      title: 'Total facturado',
      field: 'totalBilled',
      type: 'numeric',
      render: (r) => <C2Cell primary={formatCop(r.totalBilled)} secondary={`${r.totalDrivers} conductores en su red`} />,
    },
    {
      title: 'Bono ganado',
      field: 'totalBonus',
      type: 'numeric',
      render: (r) => <C2Cell primary={formatCop(r.totalBonus)} color={r.totalBonus ? '#15803D' : C2.muted} />,
    },
    ...exportColumns,
  ];

  const monthLabel = month ? api.reportMonthLabel(month) : '—';

  return (
    <>
      {error ? (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: '12px', fontFamily: C2_FONT }}
          action={
            <C2SecondaryButton size="small" onClick={load}>
              Reintentar
            </C2SecondaryButton>
          }
        >
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <C2SummaryCard icon={HubOutlinedIcon} label="Conductores con red" value={summary.drivers} caption={city ? 'En la ciudad seleccionada' : 'Tienen al menos un referido'} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <C2SummaryCard icon={GroupsOutlinedIcon} label="Conductores referidos" value={summary.referred} caption="Nivel 1 de algún conductor" />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <C2SummaryCard
            icon={AccountBalanceWalletOutlinedIcon}
            label="Recargas de la red"
            value={formatCop(summary.recharges)}
            caption={`${summary.rechargeCount} recargas · ${monthLabel}${city ? ' · todas las ciudades' : ''}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <C2SummaryCard icon={VolunteerActivismOutlinedIcon} label="Bonos de red pagados" value={formatCop(summary.bonus)} caption={monthLabel} accent="#16A34A" />
        </Grid>
      </Grid>

      <C2ReportTable
        title="Red por niveles"
        subtitle="Por cada conductor: cuántos conductores tiene en cada nivel de su red y cuánto recargaron (facturado) en el mes, junto con el bono que ganó por nivel."
        columns={columns}
        data={rows}
        loading={loading}
        emptyIcon={HubOutlinedIcon}
        emptyTitle={report ? 'Sin conductores con red' : 'Cargando reporte…'}
        emptyMessage={report ? 'Cuando un conductor refiera a otros conductores, su red aparecerá aquí.' : null}
        csvFileName={`red_por_niveles_${month || 'mes'}.csv`}
        toolbarExtras={[
          <C2Select key="m" id="network-report-month" label="Mes" value={month} onChange={(e) => setMonth(e.target.value)} options={monthOpts.length ? monthOpts : [{ value: '', label: '—' }]} minWidth={180} />,
          <C2Select key="c" id="network-report-city" label="Ciudad" value={city} onChange={(e) => setCity(e.target.value)} options={cityOpts} minWidth={190} />,
          <C2SecondaryButton key="r" size="small" startIcon={<RefreshRoundedIcon />} onClick={load} disabled={loading} sx={{ height: 40 }}>
            Actualizar
          </C2SecondaryButton>,
        ]}
        actions={[
          {
            icon: () => <C2DocsChip icon={CalendarMonthOutlinedIcon} label="Ver meses" />,
            tooltip: 'Ver el detalle de los últimos 12 meses',
            onClick: (event, row) => setDetail(row.driver),
          },
        ]}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1, px: 0.5 }}>
          <C2StatusChip tone="neutral" dot={false} label="Nivel 1: sus referidos directos" />
          <C2StatusChip tone="neutral" dot={false} label="Nivel 2: referidos de sus referidos" />
          <C2StatusChip tone="neutral" dot={false} label="Nivel 3: el siguiente nivel" />
          <C2StatusChip tone="neutral" dot={false} label="Facturado: recargas de billetera pagadas por esos conductores" />
          <C2StatusChip tone="neutral" dot={false} label="Bono: 8% / 6% / 2%, incluye recargas de usuarios referidos" />
        </Box>
        {report ? (
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.74rem', color: C2.muted, px: 0.5, mb: 0.5 }}>
            Cantidad de conductores = red actual. Generado {moment(report.generatedAt).format('lll')}.
          </Typography>
        ) : null}
      </C2ReportTable>

      <C2Dialog
        open={!!detail}
        onClose={() => setDetail(null)}
        title={detail ? `Red de ${detail.name}` : ''}
        icon={HubOutlinedIcon}
        maxWidth={820}
        actions={<C2SecondaryButton onClick={() => setDetail(null)}>Cerrar</C2SecondaryButton>}
      >
        {detail && report ? <MonthlyBreakdown driver={detail} months={report.months} /> : null}
      </C2Dialog>
    </>
  );
}
